import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Flame } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  useSEO({ title: "Donation Received | Neem Karoli Baba Mandir", noindex: true });
  const [state, setState] = useState("loading");

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      if (!sessionId) { setState("error"); return; }
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setState("paid"); return; }
      } catch (e) { /* keep polling briefly */ }
      attempts += 1;
      if (attempts < 10) timer = setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4">
      <div className="max-w-md w-full text-center bg-white border border-amber-900/15 rounded-2xl p-10 shadow-xl shadow-amber-900/5">
        <Flame className="flame mx-auto h-10 w-10 text-amber-600 mb-4" />
        {state === "loading" && (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-amber-700 mb-4" data-testid="payment-confirm-spinner" />
            <h1 className="font-display text-2xl text-amber-950 mb-2">Confirming your seva…</h1>
            <p className="text-stone-600 text-sm">Please hold on while we confirm your donation.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-700 mb-4" data-testid="payment-confirmed-icon" />
            <h1 className="font-display text-2xl text-amber-950 mb-3" data-testid="payment-success-heading">Jai Shri Ram! Thank You</h1>
            <p className="font-quote text-lg italic text-stone-700 mb-4">
              "The best service you can do is to keep your thoughts on God."
            </p>
            <p className="text-stone-600 text-sm mb-6">
              Your donation toward the new temple for Baba in Denison has been received. May Maharaj-ji's blessings be with you and your family.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <h1 className="font-display text-2xl text-amber-950 mb-3" data-testid="payment-pending-heading">Donation Processing</h1>
            <p className="text-stone-600 text-sm mb-6">
              Your payment is being processed by the bank. You will receive an email receipt shortly.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="font-display text-2xl text-amber-950 mb-3" data-testid="payment-error-heading">Something went wrong</h1>
            <p className="text-stone-600 text-sm mb-6">We could not find this donation session. If you were charged, please contact the temple.</p>
          </>
        )}
        <Link to="/">
          <Button data-testid="back-to-temple-button" className="bg-amber-700 hover:bg-amber-800 text-white rounded-full px-8">
            Return to the Mandir
          </Button>
        </Link>
      </div>
    </div>
  );
}
